/**
 * Bootstrap da home (compressor).
 *
 * Monta i18n + lang picker (via page-setup) + dropzone + file-list + toast + download.
 * Conecta drop → file-list → queue (worker pool) → atualiza status.
 */
import '@/styles/main.css';

import { t, onLangChange } from '@/i18n/index.js';
import { mountDropzone } from '@/ui/dropzone.js';
import { mountFileList, _setListHandle } from '@/ui/file-list.js';
import { mountToast, showToast } from '@/ui/toast.js';
import { mountDownload } from '@/ui/download.js';
import { getQueue } from '@/utils/queue.js';
import { setupPageShell } from '@/ui/page-setup.js';

function init() {
  // 1. Setup comum: i18n + lang picker + applyTranslations + onLangChange
  setupPageShell({
    onLangChange: () => refreshProgress(),
  });

  // 2. Toast container
  const toastContainer = document.querySelector('.toast-container');
  mountToast(toastContainer);

  // 3. File list
  const listEl = document.querySelector('.file-list');
  const queue = getQueue();
  const fileList = mountFileList(listEl, {
    onRemove: (id) => {
      cancelled.add(id);
    },
  });
  _setListHandle(fileList);
  const cancelled = new Set();

  // 4. Drop zone
  const dropEl = document.querySelector('.dropzone');
  mountDropzone(dropEl, (validFiles) => {
    const created = fileList.add(validFiles);
    processQueue(created);
  });

  // 5. Download button (ZIP)
  const downloadBtn = document.querySelector('.download-button');
  const download = mountDownload(downloadBtn, { getDone: () => fileList.getDone() });

  // 6. Progress indicator
  const progressEl = document.querySelector('.progress-indicator');
  const progressText = progressEl?.querySelector('[data-role="progress-text"]');

  function refreshProgress() {
    if (!progressEl || !progressText) return;
    const { total, processing, done, error } = fileList.getStats();
    if (total === 0) {
      progressEl.dataset.visible = 'false';
      return;
    }
    progressEl.dataset.visible = 'true';
    const finished = done + error;
    if (processing > 0) {
      progressText.textContent = t('progress.processing', { done: finished, total });
    } else if (error > 0) {
      progressText.textContent = t('progress.doneWithError', { done, error });
    } else {
      progressText.textContent = t('progress.done', { done, total });
    }
  }

  console.info('compress: ready (home)');

  async function processQueue(entries) {
    for (const entry of entries) {
      fileList.setStatus(entry.id, 'processing');
      download.refresh();
      refreshProgress();
      try {
        const result = await queue.enqueue({ type: entry.type, buffer: entry.buffer });
        entry.buffer = null;
        if (cancelled.has(entry.id)) continue;
        fileList.setStatus(entry.id, 'done', {
          compressedSize: result.compressedSize,
          _compressedBuffer: result.buffer,
        });
        download.refresh();
        refreshProgress();
      } catch (err) {
        if (cancelled.has(entry.id)) continue;
        fileList.setStatus(entry.id, 'error', { error: err.message });
        download.refresh();
        refreshProgress();
        showToast({ message: err.message, type: 'error' });
      }
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
